import "./AHUDesigner.css";
import * as THREE from "three";
import Viewport from "./Viewport";
import { toast } from "react-toastify";
import {
  ContactShadows,
  OrbitControls,
  OrthographicCamera,
  PerspectiveCamera,
  axesHelper,
} from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useEffect, useReducer, useRef, useState } from "react";
import {
  COMPONENT_TYPES,
  DIRECTION_EXTRACT,
  DIRECTION_SUPPLY,
  COMPONENT_ALIGNMENT,
  TOOL_MODE,
  DOOR_HINGE,
  ACCESS_SIDE,
} from "./Constants";
import Unit from "./Components/Unit";
import frameReducer from "./Store/frameReducer";
import {
  fromMM,
  round,
  toggleHasFrame,
  align,
  getFrameBounds,
  prltcDebugLog,
} from "./Utils/frameUtils";
import { useStore } from "./Store/zustandStore";
import Dimensions from "./Components/Dimensions";
import Header from "./Header";

function convert(data) {
  if (!data) return [];

  const { frames, ventilationUnitHeight, frameThickness } = data;

  let position = [
    0,
    fromMM(ventilationUnitHeight) / 2 + fromMM(100) + fromMM(frameThickness),
    0,
  ];
  let newFrames = [];
  let totalLength = 0;

  let currentLeftId = null;
  let currentRightId = null;

  let nextLeftId = THREE.MathUtils.generateUUID();
  let nextRightId = THREE.MathUtils.generateUUID();

  let prevLeftAttachedId = null;
  let prevRightAttachedId = null;

  for (let i = 0; i < frames.length; i++) {
    currentLeftId = nextLeftId;
    currentRightId = nextRightId;

    nextLeftId = i == frames.length - 1 ? null : THREE.MathUtils.generateUUID();
    nextRightId = THREE.MathUtils.generateUUID();

    let firstComponent = i == 0;
    let lastComponent = i == frames.length - 1;

    const {
      component,
      componentInstance,
      componentHeight,
      componentLength,
      componentWidth,
      height,
      length,
      width,
      guid,
      sectionId,
      doorType,
      doorLength,
      rows,
      inletType,
      accessSide,
      renderDoor,
      hasDoor,
      unitMounting,
    } = frames[i];

    position[0] += fromMM(length / 2) + fromMM(frameThickness);

    const frame = {
      // id: THREE.MathUtils.generateUUID(),
      id: guid,
      sectionId: sectionId,
      position: [...position],
      length: length,
      height: height,
      width: width,
      connections: connections[0].map((c, idx) => {
        return {
          ...c,
          id:
            c.side == COMPONENT_ALIGNMENT.LEFT ? currentLeftId : currentRightId,
          attachedTo:
            c.side == COMPONENT_ALIGNMENT.LEFT
              ? prevRightAttachedId
              : nextLeftId,
        };
      }),
      hasLeftFrame: true,
      hasRightFrame: true,
      hasFrame: true,
      hasFloor: true,
      hasCeiling: true,
      hasLeftWall: false, // i == 0,
      hasRightWall: false,
      hasFlange:
        i == 0 ||
        component == COMPONENT_TYPES.ESP ||
        component == COMPONENT_TYPES.FAN,
      flangeAlignment: i == 0 ? -1 : 1,
      accessSide: accessSide == "LEFT" ? ACCESS_SIDE.LEFT : ACCESS_SIDE.RIGHT,
      door: {
        visible: hasDoor,
        //doorLength
        length: doorLength,
        renderDoor: renderDoor,
        // : 250 > length
        // ? length
        // : 250,
        //hinge: component == COMPONENT_TYPES.ESP ? DOOR_HINGE.LIFT : DOOR_HINGE.LEFT,
        hinge:
          doorType == "Louvre" || doorType == "Lift"
            ? DOOR_HINGE.LIFT
            : DOOR_HINGE.LEFT,
        louvered: doorType == "Louvre",
      },
      component: component,
      componentAlignment:
        component == COMPONENT_TYPES.FILTER ||
          component == COMPONENT_TYPES.HEPA_FILTER ||
          component == COMPONENT_TYPES.CARBON_FILTER_V ||
          component == COMPONENT_TYPES.CARBON_FILTER ||
          component == COMPONENT_TYPES.CARBON_FILTER_C ||
          component == COMPONENT_TYPES.BAG_FILTER ||
          component == COMPONENT_TYPES.UV_LIGHTS
          ? COMPONENT_ALIGNMENT.LEFT
          : component == COMPONENT_TYPES.FAN
            ? COMPONENT_ALIGNMENT.RIGHT
            : undefined,
      componentInstance: componentInstance,
      baseFrame: {
        visible: true,
        height: 100,
        length: 0,
        width: 0,
        positionX: 0,
        positionY: 0,
      },
      rows: rows ?? [1],
      flange: {
        typeOfFlange: inletType,
      },
      innerComponents: [],
      isExterior: false,
      isCeilingMounted: unitMounting == "CEILING SUSPENDED" ? true : false,
      frameThickness: frameThickness,
      dimension: null,
      // excludeProjections: firstComponent || lastComponent ? null : ["right", "left"]
    };

    //prevLeftAttachedId = i == frames.length - 2 ? null : currentLeftAttachedId;
    prevRightAttachedId = currentRightId;

    switch (component) {
      case COMPONENT_TYPES.ESP:
        {
          const {
            hasAutoWasher,
            controlBoxWidth,
            controlBoxLength,
            controlBoxHeight,
            noOfRows,
            unitsPerRow,
            isESPExterior,
            coneDistance,
            hasFeet,
            feetHeight,
            isDoublePass
          } = frames[i];
          frame.esp = {
            width: componentWidth,
            length: componentLength,
            height: componentHeight,
            hasAutoWasher: hasAutoWasher,
            controlBoxWidth: controlBoxWidth,
            controlBoxLength: controlBoxLength,
            controlBoxHeight: controlBoxHeight,
            rows: noOfRows,
            columns: unitsPerRow,
            isESPExterior: isESPExterior,
            coneDistance: coneDistance,
            hasFeet: hasFeet,
            hasDrain: isESPExterior,
            feetHeight: fromMM(feetHeight),
            backControlBoxWidth: 125,
            isDoublePass: !!isDoublePass
          };
          frame.componentAlignment = COMPONENT_ALIGNMENT.CENTER;
          frame.isExterior = frame.esp.isESPExterior;
          if (frame.isExterior) {
            frame.position[0] += fromMM(frameThickness);
          }

          const totalLength =
            fromMM(frame.esp.length) + fromMM(frame.esp.coneDistance || 0);
          const espXOffset =
            align(
              0,
              fromMM(frame.length),
              fromMM(frame.esp.length),
              frame.componentAlignment,
            ) -
            fromMM(frame.esp.coneDistance || 0) / 2 +
            fromMM(frameThickness) / 2;

          frame.hasFlange = !isESPExterior;
          frame.hasFrame = !isESPExterior;
          frame.hasLeftFrame = !isESPExterior;
          frame.hasRightFrame = !isESPExterior;
          frame.hasTopFrame = !isESPExterior;
          frame.hasBottomFrame = !isESPExterior;
          frame.baseFrame.visible = !frame.esp.hasFeet;

          if (isESPExterior) {
            frame.door.visible = false;
          }

          if (isESPExterior && frame.baseFrame.visible) {
            frame.baseFrame.length =
              frame.esp.length * (frame.esp.isDoublePass ? 2 : 1);
            frame.baseFrame.width = frame.esp.width - frame.esp.backControlBoxWidth;
            frame.baseFrame.height = 100;
            frame.baseFrame.positionX = espXOffset;
            frame.baseFrame.positionY =
              -(fromMM(frame.height) / 2 + fromMM(frame.baseFrame.height) / 2) -
              //-0.005
              fromMM(frameThickness);
          }

          if (isESPExterior) {
            frame.innerComponents = generateComponentDimensions(
              component,
              frame,
              width,
              frameThickness,
            );
          }
        }

        break;
      case COMPONENT_TYPES.UV_LIGHTS:
        {
          const {
            unitsPerRow,
            noOfRows,
            controlBoxWidth,
            controlBoxLength,
            controlBoxHeight,
          } = frames[i];
          frame.uv = {
            tubeWidth: componentWidth,
            width: componentWidth,
            length: componentLength,
            height: componentHeight,
            rows: noOfRows,
            columns: unitsPerRow,
            boxWidth: controlBoxWidth,
            boxLength: controlBoxLength,
            boxHeight: controlBoxHeight,
          };
        }
        break;

      case COMPONENT_TYPES.ODOUR_NEUTRALISER:
        {
          const {
            controlBoxWidth,
            controlBoxLength,
            controlBoxHeight,
            hasOppositePair,
          } = frames[i];
          frame.odour = {
            boxWidth: controlBoxWidth,
            boxLength: controlBoxLength,
            boxHeight: controlBoxHeight,
            hasOppositePair: hasOppositePair,
          };
        }
        break;
      case COMPONENT_TYPES.OZONE:
        {
          const {
            controlBoxWidth,
            controlBoxLength,
            controlBoxHeight,
            hasOppositePair,
          } = frames[i];
          frame.ozone = {
            boxWidth: controlBoxWidth,
            boxLength: controlBoxLength,
            boxHeight: controlBoxHeight,
            hasOppositePair: hasOppositePair,
          };
        }
        break;

      case COMPONENT_TYPES.FILTER:
      case COMPONENT_TYPES.BAG_FILTER:
      case COMPONENT_TYPES.HEPA_FILTER:
      case COMPONENT_TYPES.CARBON_FILTER:
        {
          const { arrayRows, arrayColumns, component } = frames[i];
          frame.filter = {
            width: componentWidth,
            length: componentLength,
            height: componentHeight,
            rows: arrayRows,
            columns: arrayColumns,
            component: component,
          };
        }
        break;
      case COMPONENT_TYPES.CARBON_FILTER_V:
        {
          const { arrayRows, arrayColumns, component } = frames[i];
          frame.carbonFilter = {
            width: COMPONENT_TYPES.CARBON_FILTER_V ? width : componentWidth,
            length: COMPONENT_TYPES.CARBON_FILTER_V ? length : componentLength,
            height: componentHeight,
            rows: arrayRows,
            columns: arrayColumns,
            component: component,
            depth: componentLength,
          };
        }
        break;
      case COMPONENT_TYPES.CARBON_FILTER_C:
        {
          const {
            arrayRows,
            arrayColumns,
            noOfCylinders,
            component,
            arrayCylinders,
          } = frames[i];
          frame.carbonCylindricalFilter = {
            width: componentWidth,
            length: componentLength,
            height: componentHeight,
            rows: arrayRows,
            columns: arrayColumns,
            numberOfCylinders: noOfCylinders,
            component: component,
            numberOfCylinders: arrayCylinders,
            plateWidth: width,
            plateHeight: height,
            cylinderLength: componentLength,
          };
        }
        break;
      case COMPONENT_TYPES.FAN: {
        const {
          fanAngle,
          fanWidth,
          fanOutletHeight,
          fanOutletWidth,
          fanOutletLength,
          fanHeight,
          fanChannelHeight,
          baseChannelHeight,
          isolatorHeight,
          motorFrameSize,
          isFanExterior,
          isBoxFan,
          coneDistance: fanConeLength,
          boxHeight,
          boxWidth,
          boxLength,
          coneGap,
          boxInletHeight,
          boxOutletHeight,
          boxOutletWidth,
          mountingBarPitch,
          mountingBarHoleSpacing,
          mountingBarLength
        } = frames[i];

        const fanIsExterior = isFanExterior ?? false;
        frame.isExterior = fanIsExterior;
        frame.hasFlange = !fanIsExterior;
        frame.hasFrame = !fanIsExterior;
        frame.hasLeftFrame = !fanIsExterior;
        frame.hasRightFrame = !fanIsExterior;
        frame.hasTopFrame = !fanIsExterior;
        frame.hasBottomFrame = !fanIsExterior;
        frame.baseFrame.visible = !fanIsExterior;
        frame.fan = {
          angle: fanAngle,
          outletHeight: fanOutletHeight > 0 ? fanOutletHeight : null,
          outletLength: fanOutletLength > 0 ? fanOutletLength : null,
          size: fanHeight > 0 ? fanHeight : null,
          width: fanOutletWidth > 0 ? fanOutletWidth : null,
          distanceFromTop:
            fanHeight > 0
              ? height -
              (fanHeight +
                fanChannelHeight +
                baseChannelHeight +
                isolatorHeight)
              : null,
          mountingFrameLength:
            fanHeight > 0 && motorFrameSize > 0
              ? fanHeight +
              motorFrameSize +
              (length - (fanHeight + motorFrameSize)) * 0.6
              : length * 0.8,
          fanChannelHeight: fanChannelHeight,
          baseChannelHeight: baseChannelHeight,
          isolatorHeight: isolatorHeight,
          motorRadius: motorFrameSize,
          isExternal: fanIsExterior,
          isBoxFan: isBoxFan ?? false,
          coneLength: fanConeLength > 0 ? fanConeLength : null,
          // BOX-T-HT box-fan dimensions (consumed by BoxFan.js). Same
          // fall back to its own defaults.
          boxHeight: boxHeight > 0 ? boxHeight : null,
          boxWidth: boxWidth > 0 ? boxWidth : null,
          boxLength: boxLength > 0 ? boxLength : null,
          coneGap: coneGap > 0 ? coneGap : null,
          // Connector openings: the inlet the cone lands on is square
          // (Fans.InletHeight); the discharge is OutletHeight x OutletWidth.
          boxInletHeight: boxInletHeight > 0 ? boxInletHeight : null,
          boxOutletHeight: boxOutletHeight > 0 ? boxOutletHeight : null,
          boxOutletWidth: boxOutletWidth > 0 ? boxOutletWidth : null,
          // BOX-T-HT mounting bars. mountingBarPitch is bar to bar ALONG the
          // fan; mountingBarHoleSpacing (hole to hole on one bar) and
          // mountingBarLength (the full bar) are both measured ACROSS it.
          mountingBarPitch: mountingBarPitch > 0 ? mountingBarPitch : null,
          mountingBarHoleSpacing:
            mountingBarHoleSpacing > 0 ? mountingBarHoleSpacing : null,
          mountingBarLength: mountingBarLength > 0 ? mountingBarLength : null
        };

        // External BOX-T-HT box fan: this frame draws no corner posts
        // (hasFrame/hasLeftFrame = false), so unlike an in-frame section it does
        // not fill the frameThickness of clearance the layout loop leaves on
        // each side. That left a frameThickness-wide hole between the AHU body's
        // right bound and this frame's left bound, showing up as a break in the
        // dimension chain just before the cone (500) callout. Pull the frame back
        // by one frame thickness so the two bounds coincide and the chain reads
        // continuously — the mirror of the exterior ESP above, which adds a
        // frame thickness for the same reason at the inlet end.
        if (fanIsExterior && (isBoxFan ?? false)) {
          frame.position[0] -= fromMM(frameThickness);

          // This frame is just the cone run and the box — there is no walk-in
          // casing here, so the access door the backend reports for the section
          // has nothing to open onto. Drop it (Panel gates on door.visible).
          frame.door.visible = false;
        }

        if (fanIsExterior && !(isBoxFan ?? false)) {
          frame.door.visible = false;
        }

        frame.componentAlignment = frame.flangeAlignment =
          fanAngle == 90 ? COMPONENT_ALIGNMENT.TOP : COMPONENT_ALIGNMENT.RIGHT;

        // TEMP DEBUG: dump the raw backend source values vs the resolved values
        // (and whether each fell back to the PRLTCFan.js default) so we can see
        // which dimensions are real and which are placeholders.
        // Defaults here MUST mirror PRLTCFan.js.
        if (fanIsExterior) {
          const PRLTC_DEFAULTS = {
            fanWidth: 322, // fan.width  (outlet width)
            fanHeight: 850, // fan.size   (fan body length / scroll diameter)
            fanOutletHeight: 622,
            fanOutletLength: 70,
            coneLength: 500,
            motorRadius: 80, // motorFrameSize
            isolatorHeight: 40,
          };
          const resolve = (raw, def) => ({
            raw: raw ?? null,
            used: raw > 0 ? raw : def,
            usingDefault: !(raw > 0),
            default: def,
          });
          prltcDebugLog({
            kind: "fan-source-values",
            frameId: frame.id,
            componentAlignment: frame.componentAlignment,
            raw: {
              fanAngle,
              fanWidth,
              fanOutletWidth,
              fanOutletHeight,
              fanOutletLength,
              fanHeight,
              fanChannelHeight,
              baseChannelHeight,
              isolatorHeight,
              motorFrameSize,
              coneLength: fanConeLength,
              isFanExterior,
              isBoxFan,
              // Box-fan geometry straight off the wire. All six come from the
              // Fans row (FanHeight/FanWidth/FanLength/InletHeight/OutletHeight/
              // OutletWidth); a 0 or null here means the backend never filled the
              // row in, not that the designer dropped it.
              boxHeight,
              boxWidth,
              boxLength,
              boxInletHeight,
              boxOutletHeight,
              boxOutletWidth,
              coneGap,
            },
            resolved: {
              outletWidth: resolve(fanOutletWidth, PRLTC_DEFAULTS.fanWidth),
              fanBodyLength: resolve(fanHeight, PRLTC_DEFAULTS.fanHeight),
              outletHeight: resolve(
                fanOutletHeight,
                PRLTC_DEFAULTS.fanOutletHeight,
              ),
              outletLength: resolve(
                fanOutletLength,
                PRLTC_DEFAULTS.fanOutletLength,
              ),
              coneLength: resolve(fanConeLength, PRLTC_DEFAULTS.coneLength),
              motorRadius: resolve(motorFrameSize, PRLTC_DEFAULTS.motorRadius),
              isolatorHeight: resolve(
                isolatorHeight,
                PRLTC_DEFAULTS.isolatorHeight,
              ),
            },
          });
        }


        frame.dimension = {
          visibleAxes: ["x"],
        };

        frame.innerComponents = generateComponentDimensions(
          component,
          frame,
          width,
          frameThickness,
        );

        break;
      }
      default:
        break;
    }

    // frame.innerComponents = generateComponentDimensions(
    //   component,
    //   frame,
    //   width,
    //   frameThickness,
    // );

    newFrames.push(frame);
    position[0] += fromMM(length / 2) + fromMM(frameThickness);

    position[0] = round(position[0], 3);

    totalLength += length;
  }

  for (let i = 0; i < newFrames.length; i++) {
    const frame = newFrames[i];
    frame.position[0] -= fromMM(totalLength / 2);
  }

  // return calculateAttached(newFrames);

  // let finalFrames = calculateAttached(newFrames);

  const sectionIds = [
    ...new Set(
      newFrames.filter((f) => f.sectionId != null).map((f) => f.sectionId),
    ),
  ];

  for (let i = 0; i < sectionIds.length; i++) {
    const sectionFrames = newFrames.filter(
      (f) => f.sectionId === sectionIds[i],
    );

    newFrames = toggleHasFrame(
      true,
      COMPONENT_ALIGNMENT.LEFT,
      newFrames,
      [sectionFrames[0].id],
      frameThickness,
    );
    newFrames = toggleHasFrame(
      false,
      COMPONENT_ALIGNMENT.RIGHT,
      newFrames,
      [sectionFrames[0].id],
      frameThickness,
    );

    newFrames = toggleHasFrame(
      false,
      COMPONENT_ALIGNMENT.LEFT,
      newFrames,
      [sectionFrames[sectionFrames.length - 1].id],
      frameThickness,
    );
    newFrames = toggleHasFrame(
      true,
      COMPONENT_ALIGNMENT.RIGHT,
      newFrames,
      [sectionFrames[sectionFrames.length - 1].id],
      frameThickness,
    );

    if (sectionFrames.length > 2) {
      for (let j = 1; j < sectionFrames.length - 1; j++) {
        newFrames = toggleHasFrame(
          false,
          COMPONENT_ALIGNMENT.LEFT,
          newFrames,
          [sectionFrames[j].id],
          frameThickness,
        );
        newFrames = toggleHasFrame(
          false,
          COMPONENT_ALIGNMENT.RIGHT,
          newFrames,
          [sectionFrames[j].id],
          frameThickness,
        );
      }
    }
  }

  return newFrames;
}

function generateComponentDimensions(
  component,
  frame,
  frameWidth,
  frameThickness,
) {
  switch (component) {
    case COMPONENT_TYPES.FILTER:
    case COMPONENT_TYPES.FILTER:
    case COMPONENT_TYPES.BAG_FILTER:
    case COMPONENT_TYPES.HEPA_FILTER:
    case COMPONENT_TYPES.CARBON_FILTER:
    case COMPONENT_TYPES.CARBON_FILTER_V:
    case COMPONENT_TYPES.CARBON_FILTER_C: {
      const bounds = getFrameBounds([frame], frameThickness);

      const dimensions = [];
      const preFilter = frame.filter;
      const id =
        component === COMPONENT_TYPES.FILTER
          ? "pre-filter"
          : component === COMPONENT_TYPES.BAG_FILTER
            ? "bag-filter"
            : component === COMPONENT_TYPES.HEPA_FILTER
              ? "hepa-filter"
              : "carbon-filter";
      dimensions.push({
        id: id,
        type: "dimension",
        boundingBox: {
          position: [
            0,
            0,
            align(
              0,
              fromMM(frameWidth),
              fromMM(frame.width),
              COMPONENT_ALIGNMENT.BOTTOM,
            ),
          ],
          size: bounds.size,
          //  [
          //   fromMM(frame.length),
          //   fromMM(frame.height),
          //   fromMM(frame.width),
          // ],
        },
        views: {
          "3d": {
            visibleAxes: ["x1"],
            levels: [1, 1, 1],
          },
          "2d": {
            visibleAxes: ["x"],
            levels: [1, 1, 1],
          },
        },
        useFullFrame: true,
      });
      return dimensions;
      break;
    }
    // case COMPONENT_TYPES.UV_LIGHTS:
    //   return [
    //     {
    //       id: "uv-box-1",
    //       type: "dimension",
    //       boundingBox: {
    //         position: [
    //           0,
    //           0,
    //           align(
    //             fromMM(-frameThickness) - fromMM(UVProperties.boxWidth),
    //             fromMM(frameWidth),
    //             fromMM(UVProperties.boxWidth),
    //             COMPONENT_ALIGNMENT.BOTTOM,
    //           ),
    //         ],
    //         size: [
    //           fromMM(UVProperties.boxLength),
    //           fromMM(UVProperties.boxHeight),
    //           fromMM(UVProperties.boxWidth),
    //         ],
    //       },
    //       views: {
    //         "3d": {
    //           visibleAxes: ["x2"],
    //           levels: [2, 4, 1],
    //         },
    //         "2d": {
    //           visibleAxes: ["x"],
    //           levels: [1],
    //         },
    //       },
    //       useFullFrame: false,
    //     },
    //   ];

    case COMPONENT_TYPES.ESP:
      const esp = frame.esp || {};
      const passCount = esp.isDoublePass ? 2 : 1;
      const { baseFrame } = frame;
      const dimensions = [];
      const frameLength = fromMM(frame.length);
      const frameHeight = fromMM(frame.height);
      const espLength = fromMM(esp.length) * passCount;
      const t = fromMM(esp.isESPExterior ? 0 : frameThickness);
      const fw = fromMM(frameWidth);
      const fh = fromMM(frame.height);
      const espL = fromMM(esp.length) * passCount;
      const espH = fromMM(esp.height);
      const espW = fromMM(esp.width);
      const espBaseHeight = esp.isESPExterior ? 0 : fromMM(30);
      const baseframeOrFeetHeight =
        esp.feetHeight > 0 ? esp.feetHeight : fromMM(100);
      const espXOffset =
        align(0, frameLength, espLength, COMPONENT_ALIGNMENT.LEFT) +
        fromMM(frameThickness / 2);

      //const espYOffset = -(t + espH / 2 + fh / 2 + esp.feetHeight/2 + fromMM(100)/2);
      // const espYOffset =  align(
      //         (fh/2 + fromMM(frameThickness)) - (fh/2 + fromMM(frameThickness) + esp.feetHeight),
      //         fromMM(frameHeight),
      //         fromMM(espH),
      //         COMPONENT_ALIGNMENT.BOTTOM,
      //)

      const espYOffset = align(
        0,
        frameHeight + fromMM(frameThickness * 2) + fromMM(100) * 2,
        espH + baseframeOrFeetHeight,
        COMPONENT_ALIGNMENT.BOTTOM,
      );

      dimensions.push({
        id: "esp-box-1",
        type: "dimension",
        boundingBox: {
          position: [
            espXOffset,
            espYOffset,
            align(
              0,
              fromMM(frameWidth),
              fromMM(frameWidth) + fromMM(esp.backControlBoxWidth || 0),
              COMPONENT_ALIGNMENT.BOTTOM,
            ),
          ],
          size: [espL, espH + baseframeOrFeetHeight + t, espW],
        },
        views: {
          "3d": {
            // y1 (left/inlet side) so the ESP height (incl. feet) shows on the
            // far left, matching 2D. Was y2 (right), where it resolved to a
            // hidden side in 3D and the ESP height went missing.
            visibleAxes: ["x1", "y1", "z1"],
            levels: [1, 1, 1],
            alignments: {
              length:
                espH > frameHeight * 1.2
                  ? COMPONENT_ALIGNMENT.BOTTOM
                  : COMPONENT_ALIGNMENT.TOP,
            },
            // dimensionSides: {
            //   width: [0, 0, -1],
            //   height: [0, 0, 0],
            //   length: [0, 0, 0],
            // },
          },
          "2d": {
            visibleAxes: ["x1", "y1", "z1"],
            levels: [1, 1, 1],
            // dimensionSides: {
            //   width: [0, 0, -1],
            //   height: [-1, 0, 0],
            //   length: [0, 0, 0],
            // },
          },
        },
        useFullFrame: true,
        ignoreHidden: true,
      });

      if (esp.coneDistance && esp.coneDistance > 0) {
        const coneLen = fromMM(esp.coneDistance);
        dimensions.push({
          id: "esp-cone-outlet",
          type: "dimension",
          boundingBox: {
            position: [
              espL / 2,
              -0.17,
              align(0, fw, espW, COMPONENT_ALIGNMENT.BOTTOM),
            ],
            size: [coneLen, 0, 0],
          },
          views: {
            "3d": {
              visibleAxes: ["x"],
              levels: [1, 0, 0],
              alignments: {
                length:
                  espH > frameHeight * 1.2
                    ? COMPONENT_ALIGNMENT.BOTTOM
                    : COMPONENT_ALIGNMENT.TOP,
              },
            },
            "2d": { visibleAxes: ["x"], levels: [1, 1, 1] },
          },
          useFullFrame: false,
          ignoreHidden: false,
        });
      }
      return dimensions;
    // return [
    //   {
    //     id: "esp-box-1",
    //     type: "dimension",
    //     boundingBox: {
    //       position: [
    //         0,
    //         -0.17,
    //         align(
    //           0,
    //           fromMM(frameWidth),
    //           fromMM(esp.width),
    //           COMPONENT_ALIGNMENT.BOTTOM
    //         ),
    //       ],
    //       size: [fromMM(esp.length), fromMM(esp.height), fromMM(esp.width)],
    //     },
    //     views: {
    //       "3d": {
    //         visibleAxes: ["x2", "y2", "z2"],
    //         levels: [2, 3, 2],
    //       },
    //       "2d": {
    //         visibleAxes: ["x", "y"],
    //         levels: [2, 2],
    //       },
    //     },
    //     useFullFrame: false,
    //   },
    //   // {
    //   //   id: "esp-control-box-1",
    //   //   type: "dimension",
    //   //   boundingBox: {
    //   //     position: [0, -0.17, align(fromMM(frameThickness), fromMM(frameWidth), fromMM(ESPProperties.controlBoxWidth), COMPONENT_ALIGNMENT.TOP)],
    //   //     size: [fromMM(ESPProperties.controlBoxLength), fromMM(ESPProperties.controlBoxHeight / 2), fromMM(ESPProperties.controlBoxWidth)],
    //   //   },
    //   //   views: {
    //   //     "3d": {
    //   //       visibleAxes: ["x1", "y1", "z1"],
    //   //       levels: [2, 1, 2],
    //   //     },
    //   //     "2d": {
    //   //       visibleAxes: ["x", "y"],
    //   //       levels: [3, 3],
    //   //     },
    //   //   },
    //   //   useFullFrame: false,
    //   // },
    //   // {
    //   //   id: "esp-outlet-1",
    //   //   type: "dimension",
    //   //   boundingBox: {
    //   //     position: [fromMM(ESPProperties.length) / 2, -0.175, 0.025],
    //   //     size: [0.02, 0.4, 0.6],
    //   //   },
    //   //   views: {
    //   //     "3d": {
    //   //       visibleAxes: ["x", "y", "z"],
    //   //       levels: [2, 2, 2],
    //   //     },
    //   //     "2d": {
    //   //       visibleAxes: ["x", "y"],
    //   //       levels: [1, 1],
    //   //     },
    //   //   },
    //   //   useFullFrame: false,
    //   // },
    //   // {
    //   //   id: "esp-inlet-1",
    //   //   type: "dimension",
    //   //   boundingBox: {
    //   //     position: [-fromMM(ESPProperties.length) / 2, -0.175, 0.025],
    //   //     size: [0.02, 0.4, 0.6],
    //   //   },
    //   //   views: {
    //   //     "3d": {
    //   //       visibleAxes: ["x", "y", "z"],
    //   //       levels: [2, 2, 2],
    //   //     },
    //   //     "2d": {
    //   //       visibleAxes: ["x", "y"],
    //   //       levels: [1, 1],
    //   //     },
    //   //   },
    //   //   useFullFrame: false,
    //   // },
    //   // {
    //   //   id: "test",
    //   //   type: "text",
    //   //   text: "ESP Control box",
    //   //   boundingBox: {
    //   //     position: [0, -0.17, align(fromMM(frameThickness), fromMM(frameWidth), fromMM(ESPProperties.controlBoxWidth), COMPONENT_ALIGNMENT.TOP)],
    //   //   },
    //   // },
    // ];
    case COMPONENT_TYPES.FAN: {
      const bounds = getFrameBounds([frame], frameThickness);
      const dimensions = [];

      // External PRL-TC fan: emit the manufacturing-drawing callouts.
      // Driven entirely off frame.fan.* values so the dimension numbers track
      // the 3D geometry built in PRLTCFan.js. Emitted as innerComponents (not the
      // frame-level box) so they survive the exterior-frame skip in
      // collectDimensionables, mirroring how the exterior ESP box is handled.
      if (frame.isExterior) {
        const fan = frame.fan || {};
        if (fan.isBoxFan) {
          // External BOX-T-HT box fan: emit the same style of callouts as the
          // PRL-TC branch below, with values tracking BoxFan.js's assembly
          // chain (frame wall > flange > cone > flexible connector > box).
          const boxWidthM = fromMM(fan.boxWidth || 1000);
          const boxLengthM = fromMM(fan.boxLength || 1000);
          const gapM = fromMM(fan.coneGap || 500);
          // Connector openings: the inlet the cone lands on is a square
          // (Fans.InletHeight); the discharge is OutletHeight x OutletWidth.
          //
          // These are read RAW, with no fallback. BoxFan.js has to fall back to a
          // placeholder because it must draw something, but a callout carrying a
          // placeholder is a fabricated manufacturing dimension — it reads exactly
          // like a real one and there is no way to tell them apart on the drawing.
          // So a cross-section is only emitted when the backend actually supplied
          // both of its values (see the crossSection helper below); a missing
          // callout is the visible signal that the Fans row is incomplete.
          const inletSizeMM = fan.boxInletHeight || 0;
          const outletHeightMM = fan.boxOutletHeight || 0;
          const outletWidthMM = fan.boxOutletWidth || 0;

          const frameLengthM = fromMM(frame.length);
          const frameHeightM = fromMM(frame.height);
          const frameWidthM = fromMM(frameWidth);
          const t = fromMM(frameThickness);

          // X stations, matching BoxFan.js exactly: the chain starts on the
          // frame's left bound (which now coincides with the AHU body's right
          // bound, see the position shift in the FAN case above) and spans the
          // full coneGap onto the box cutout.
          //
          // coneGap covers the WHOLE connection chain - outlet flange (50) +
          // cone + flexible connector (200) - as the client drawing dimensions
          // its 500. BoxFan.js divides the gap between those three; here only
          // the total is needed, since the chain is dimensioned as one figure.
          const wallX = -frameLengthM / 2;
          const cutoutFaceX = wallX + gapM;

          // Vertical centre of the box, mirroring BoxFan.js exactly. The box
          // does not hang off the frame centre - it stands on the floor, on
          // its mounting bars, inside a base frame channel:
          //     ground -> base frame channel (70, bars sit inside it) -> box
          // Height default and the frame.fan read must stay in step with
          // BASE_FRAME_HEIGHT_MM in BoxFan.js, or the drawing and the 3D model
          // will place the box at different heights.
          // When a range has no base frame the box keeps the old placement,
          // one frame thickness below frame centre, and BoxFan.js falls back
          // to its four corner feet.
          const baseFrameHeightM = fromMM(fan.baseFrameHeight ?? 70);
          const ahuBaseFrameM = fromMM(frame.baseFrame?.height || 0);
          const boxHeightM = fromMM(fan.boxHeight || 900);
          const hasBaseBarD =
            (fan.mountingBarPitch || 0) > 0 && (fan.mountingBarLength || 0) > 0;
          // Ground line, matching Frame.js's AHU base frame underside exactly
          // as BoxFan.js does - see the derivation there. Using the frame
          // centre instead left the box floating by frameThickness + 5mm.
          const groundYD = -frameHeightM / 2 - ahuBaseFrameM - 0.005;
          // Mirrors BoxFan.js: the box stands on the floor either way - on the
          // base frame channel when there is one, otherwise on its four 100mm
          // corner feet. The old fallback placed it at frame centre, which left
          // the callouts reading against a box the 3D view drew elsewhere.
          const footSizeM = fromMM(100);
          // The -t mirrors the y = -frameThickness group BoxFan.js draws into;
          // these callout positions have no such wrapper, so it is applied here.
          const boxCenterY = hasBaseBarD
            ? groundYD + baseFrameHeightM + boxHeightM / 2 - t
            : groundYD + footSizeM + boxHeightM / 2 - t;

          // The three cross-section callouts all read against the discharge end
          // and ladder outwards on their own tiers, matching the nested stacks
          // in the drawing:
          //   tier 1  outlet connector  (OutletHeight x OutletWidth)
          //   tier 2  inlet connector   (InletHeight square)
          //   tier 3  box casing        (FanHeight x FanWidth)
          // Both y2 and z2 are listed on each deliberately: in the 2D pass
          // dimensionSide is only resolved from a Y axis (calculateDimensions),
          // so a z-only item leaves dimensionSide at 0 and its width line is
          // dropped — which is why the old z2-only "boxfan-overall" never
          // rendered a width. The tier drives both the height and width offsets
          // (heightScale/widthScale are keyed off levels[1]), so bumping a tier
          // moves that callout's whole cross out of the one inside it.
          //
          // heightMM/widthMM are raw backend millimetres: a cross-section is
          // skipped unless both are present, so nothing invented is ever
          // dimensioned.
          const crossSection = (id, heightMM, widthMM, tier) => {
            if (!(heightMM > 0) || !(widthMM > 0)) return null;
            const height = fromMM(heightMM);
            const width = fromMM(widthMM);
            return {
              id,
              type: "dimension",
              boundingBox: {
                position: [
                  cutoutFaceX + boxLengthM / 2,
                  boxCenterY,
                  align(0, frameWidthM, width, COMPONENT_ALIGNMENT.CENTER),
                ],
                size: [0, height, width],
              },
              views: {
                "3d": { visibleAxes: ["y2", "z2"], levels: [1, tier, tier] },
                "2d": { visibleAxes: ["y2", "z2"], levels: [1, tier, tier] },
              },
              useFullFrame: true,
              ignoreHidden: false,
            };
          };

          dimensions.push(
            ...[
              crossSection("boxfan-connector", outletHeightMM, outletWidthMM, 1),
              crossSection("boxfan-inlet", inletSizeMM, inletSizeMM, 2),
              crossSection("boxfan-box", fan.boxHeight || 0, fan.boxWidth || 0, 3),
            ].filter(Boolean),
          );

          // The whole connection chain reads as ONE callout spanning the full
          // gap - flange + cone + flexible connector together - exactly as the
          // client drawing dimensions its 500. The individual 50/250/200 parts
          // are deliberately NOT dimensioned: they are fixed build details, and
          // splitting them out gave three stacked lines where the drawing shows
          // a single figure.
          if (gapM > 0) {
            dimensions.push({
              id: "boxfan-cone",
              type: "dimension",
              boundingBox: {
                position: [
                  wallX + gapM / 2,
                  align(0, frameHeightM, 0, COMPONENT_ALIGNMENT.BOTTOM),
                  align(0, frameWidthM, boxWidthM, COMPONENT_ALIGNMENT.BOTTOM),
                ],
                size: [gapM, 0, 0],
              },
              views: {
                "3d": { visibleAxes: ["x"], levels: [1, 1, 1] },
                "2d": { visibleAxes: ["x"], levels: [1, 1, 1] },
              },
              useFullFrame: false,
              ignoreHidden: false,
            });
          }

          dimensions.push({
            id: "boxfan-box-length",
            type: "dimension",
            boundingBox: {
              position: [
                cutoutFaceX + boxLengthM / 2,
                align(0, frameHeightM, 0, COMPONENT_ALIGNMENT.BOTTOM),
                align(0, frameWidthM, boxWidthM, COMPONENT_ALIGNMENT.BOTTOM),
              ],
              size: [boxLengthM, 0, 0],
            },
            views: {
              "3d": { visibleAxes: ["x"], levels: [1, 1, 1] },
              "2d": { visibleAxes: ["x"], levels: [1, 1, 1] },
            },
            useFullFrame: false,
            ignoreHidden: false,
          });

          return dimensions;
        }

        // Match PRLTCFan.js geometry conventions.
        const fanWidth = fromMM(fan.width || 322);
        const mountingFrameWidth = fromMM(fan.mountingFrameWidth || 80);
        const motorOutside = fan.motorOutside ?? true;
        const baseFrameHeight = fromMM(frame.baseFrame?.height || 0);

        const frameLengthM = fromMM(frame.length);
        const frameHeightM = fromMM(frame.height);
        const frameWidthM = fromMM(frameWidth);
        const t = fromMM(frameThickness);


        // Cone/transition length between the AHU duct and the fan inlet.
        // Falls back to the same default the geometry uses in PRLTCFan.js.
        const coneLength = fromMM(fan.coneLength || 500);

        // Flexible connector (3-segment: 75 + 100 + 75 mm) and fan body length,
        // matching PRLTCFan.js. The fan body inlet sits after the cone + the
        // flexible connector; the body itself spans fanSize along X.
        const flexibleConnectorLength = fromMM(75 + 100 + 75);
        const fanBodyLength = fromMM(fan.size || 850);

        // Overall unit dimensions (drawing: 1561 / 1963 / 2100).
        //  - length  = full frame length incl. outlet duct connection (x)
        //  - height  = fan housing top down to base/feet (y)
        //  - width   = fan + motor span across Z, incl. mounting frames (z)
        // Ground level matches PRLTCFan.js groupPosition math:
        //   groundLevelY = -frameHeight/2 - frameThickness - baseFrameHeight
        const overallLength = frameLengthM + t * 2;
        const groundLevelY = -frameHeightM / 2 - t - baseFrameHeight;
        const fanTopY = frameHeightM / 2;
        const overallHeight = fanTopY - groundLevelY;
        const overallWidth = motorOutside
          ? fanWidth * 2 + mountingFrameWidth * 2
          : fanWidth + mountingFrameWidth;

        dimensions.push({
          id: "prltc-overall",
          type: "dimension",
          boundingBox: {
            position: [
              0,
              (fanTopY + groundLevelY) / 2,
              align(0, frameWidthM, overallWidth, COMPONENT_ALIGNMENT.CENTER),
            ],
            size: [overallLength, overallHeight, overallWidth],
          },
          views: {
            // Width only on the fan side (the fan+motor span is fan-specific).
            // Length (x) is broken down by the cone/connector/fan-body callouts;
            // height is dimensioned at the cone (prltc-frame-at-cone), so both
            // are omitted here to avoid duplication.
            // z2 anchors to the fan/outlet end; levels[2]=5 pushes the width line
            // outward past the fan geometry and beyond the outlet-width callout
            // (which is at width tier 3).
            "3d": {
              visibleAxes: ["z2"],
              levels: [1, 4, 5],
            },
            "2d": {
              visibleAxes: ["z2"],
              levels: [1, 4, 5],
            },
          },
          useFullFrame: true,

          // ignoreHidden must stay false: when true it forces showLength on
          // (showLength = showX || ignoreHidden), which would re-draw the
          // unwanted overall length line. Height/width still render via their
          // own showY/showZ from the y/z axes above.
          ignoreHidden: false,
        });

        // (Unit cross-section height/width is drawn by the unit-total pass in
        // createSVGDimensions, which now places it on the non-external end — the
        // left/inlet side when the fan is the external right-end component — so
        // no separate frame-height callout is needed here.)

        // Outlet width (single main callout), measured across Z at the outlet
        // end. The fan body depth is fanWidth * 2 when the motor sits outside,
        // matching overallWidth's body-span derivation. (The drawing shows
        // nested 663/694/783 steps, but with this fan's geometry they differ by
        // only the ~31mm flange step and collapse onto each other, so a single
        // main outlet width is used instead.)
        const outletWidth = fanWidth * (motorOutside ? 2 : 1);
        const outletX = align(0, frameLengthM, 0, COMPONENT_ALIGNMENT.RIGHT);

        dimensions.push({
          id: "prltc-outlet-width",
          type: "dimension",
          boundingBox: {
            position: [
              outletX,
              align(0, frameHeightM, 0, COMPONENT_ALIGNMENT.BOTTOM),
              align(0, frameWidthM, outletWidth, COMPONENT_ALIGNMENT.CENTER),
            ],
            size: [0, 0, outletWidth],
          },
          views: {
            // 3D: pin the outlet width to the fan side (z2 → unitBounds right
            // end) so it sits next to the fan instead of floating on the far
            // left (the unconditional "z" resolved to the wrong side in 3D where
            // dimensionSide is view-driven).
            // 2D: keep unconditional "z" — there dimSide==0 renders it via
            // showZ12 regardless of side, and it's already placed correctly.
            // levels[1]=3 (widthScale 0.55) + levels[2]=3 push the line/label to
            // a far-outer tier so it clears the generic frame-box width (1300)
            // and the overall-width callout that also anchor at the unit ends.
            "3d": { visibleAxes: ["z2"], levels: [1, 3, 3] },
            "2d": { visibleAxes: ["z"], levels: [1, 3, 3] },
          },
          useFullFrame: false,
          ignoreHidden: false,
        });


        // Cone/transition length (drawing: the duct gap before the fan inlet).
        // The cone geometry in PRLTCFan.js spans the X range
        //   [-frameLength/2, -frameLength/2 + coneLength]
        // (wide end at the frame's inlet edge, narrow end coneLength toward the
        // fan), so its midpoint is -frameLength/2 + coneLength/2. Length-only
        // callout, mirroring the exterior ESP's esp-cone-outlet dimension.
        if (coneLength > 0) {
          dimensions.push({
            id: "prltc-cone",
            type: "dimension",
            boundingBox: {
              position: [
                -frameLengthM / 2 + coneLength / 2,
                align(0, frameHeightM, 0, COMPONENT_ALIGNMENT.BOTTOM),
                align(0, frameWidthM, overallWidth, COMPONENT_ALIGNMENT.BOTTOM),
              ],
              size: [coneLength, 0, 0],
            },
            views: {
              "3d": { visibleAxes: ["x"], levels: [1, 1, 1] },
              "2d": { visibleAxes: ["x"], levels: [1, 1, 1] },
            },
            useFullFrame: false,
            ignoreHidden: false,
          });
        }

        // The cone, flexible connector and fan body sit on consecutive X
        // segments, so their length callouts are staggered onto separate
        // vertical tiers (levels[0] = 1 / 2 / 3) to stop the labels colliding.

        // Flexible connector length, sitting between the cone narrow end and the
        // fan inlet (frame-local X range
        //   [-frameLength/2 + coneLength,
        //    -frameLength/2 + coneLength + flexibleConnectorLength]).
        if (flexibleConnectorLength > 0) {
          dimensions.push({
            id: "prltc-flex-connector",
            type: "dimension",
            boundingBox: {
              position: [
                -frameLengthM / 2 + coneLength + flexibleConnectorLength / 2,
                align(0, frameHeightM, 0, COMPONENT_ALIGNMENT.BOTTOM),
                align(0, frameWidthM, overallWidth, COMPONENT_ALIGNMENT.BOTTOM),
              ],
              size: [flexibleConnectorLength, 0, 0],
            },
            views: {
              "3d": { visibleAxes: ["x"], levels: [2, 1, 1] },
              "2d": { visibleAxes: ["x"], levels: [2, 1, 1] },
            },
            useFullFrame: false,
            ignoreHidden: false,
          });
        }

        // Fan body length, starting at the fan inlet (after the cone + flexible
        // connector) and spanning fanSize along X, matching the centrifugal body
        // built in PRLTCFan.js.
        if (fanBodyLength > 0) {
          const fanBodyStartX =
            -frameLengthM / 2 + coneLength + flexibleConnectorLength;
          dimensions.push({
            id: "prltc-fan-body",
            type: "dimension",
            boundingBox: {
              position: [
                fanBodyStartX + fanBodyLength / 2,
                align(0, frameHeightM, 0, COMPONENT_ALIGNMENT.BOTTOM),
                align(0, frameWidthM, overallWidth, COMPONENT_ALIGNMENT.BOTTOM),
              ],
              size: [fanBodyLength, 0, 0],
            },
            views: {
              "3d": { visibleAxes: ["x"], levels: [1, 1, 1] },
              "2d": { visibleAxes: ["x"], levels: [1, 1, 1] },
            },
            useFullFrame: false,
            ignoreHidden: false,
          });
        }

        return dimensions;
      }


      // dimensions.push({
      //   id: "fan",
      //   type: "dimension",
      //   boundingBox: {
      //     position: [
      //       0,
      //       align(
      //         0,
      //         fromMM(frame.height),
      //         fromMM(frame.height),
      //         COMPONENT_ALIGNMENT.CENTER,
      //       ),
      //       align(
      //         0,
      //         fromMM(frameWidth),
      //         fromMM(frame.width),
      //         COMPONENT_ALIGNMENT.BOTTOM,
      //       ),
      //     ],
      //     size: bounds.size,
      //     //  [
      //     //   fromMM(frame.length),
      //     //   fromMM(frame.height),
      //     //   fromMM(frame.width),
      //     // ],
      //   },
      //   views: {
      //     "3d": {
      //       visibleAxes: ["x1"],
      //       levels: [1, 2, 1],
      //     },
      //     "2d": {
      //       visibleAxes: ["x"],
      //       levels: [1, 2, 2],
      //       // dimensionSides: {
      //       //   width: [0, 0, 1],
      //       //   height: [0, 0, 0],
      //       //   length: [0, 0, 0],
      //       // },
      //     },
      //   },
      //   useFullFrame: false,
      // });

      const fanDistanceFromTop = fromMM(frame.fan?.distanceFromTop ?? 150);
      const flangeAlignment = frame.flangeAlignment || COMPONENT_ALIGNMENT.LEFT;
      const fanSize = fromMM(frame.fan?.size ?? 550);
      const fanOutletHeight = fromMM(frame.fan?.outletHeight ?? 422);
      const fanOutletLength = fromMM(frame.fan?.outletLength ?? 70);
      const fanFrameLength = fanSize - fanOutletLength;

      const flangeDistanceFromTop =
        flangeAlignment == COMPONENT_ALIGNMENT.TOP
          ? align(
            align(0, fanSize, fanFrameLength, COMPONENT_ALIGNMENT.LEFT) -
            frameThickness / 2 -
            fromMM(40) / 2,
            fanSize,
            fanOutletHeight,
            COMPONENT_ALIGNMENT.LEFT,
          )
          : fanDistanceFromTop;

      dimensions.push({
        id: "fan-outlet",
        type: "dimension",
        boundingBox: {
          position: [
            align(
              fromMM(frame.fan.outletLength + frameThickness),
              fromMM(frame.length),
              fromMM(frame.fan.outletLength),
              COMPONENT_ALIGNMENT.RIGHT,
            ),
            fromMM(frame.height / 2) -
            fanOutletHeight / 2 -
            flangeDistanceFromTop,
            align(
              0,
              fromMM(frameWidth),
              fromMM(frame.width),
              COMPONENT_ALIGNMENT.BOTTOM,
            ),
          ],
          //bounds.size,
          size: [
            fromMM(frame.fan.outletLength),
            fromMM(frame.fan.outletHeight),
            fromMM(frame.fan.outletHeight),
          ],
        },
        views: {
          "3d": {
            visibleAxes: ["y1", "z2"],
            levels: [1, 1, 2],
          },
          "2d": {
            visibleAxes: ["x1", "y2", "z2"],
            levels: [1, 1, 1],
            // dimensionSides: {
            //   width: [1, 0, 0],
            //   height: [0, 0, 0],
            //   length: [0, 0, 0],
            // },
          },
        },
        useFullFrame: true,
      });

      return dimensions;
      break;
    }
    default:
      return [];
  }
}

function AHUDesigner({ ahuDesignerManager }) {
  const [
    setSelectedUUID,
    toolMode,
    showDimensions,
    frameThickness,
    setFrameThickness,
    setShowAddComponentOptions,
    setSelectedConnectionMenu,
    setFramesAttachedTogether,
    setSelectedUUIDIsInSection,
    setSelectedComponentInstance,
    setFrames,
  ] = useStore((state) => [
    state.setSelectedUUID,
    state.toolMode,
    state.showDimensions,
    state.frameThickness,
    state.setFrameThickness,
    state.setShowAddComponentOptions,
    state.setSelectedConnectionMenu,
    state.setFramesAttachedTogether,
    state.setSelectedUUIDIsInSection,
    state.setSelectedComponentInstance,
    state.setFrames,
  ]);

  //setFrameThickness(ahuDesignerManager.frameThickness);

  const [frames, dispatch] = useReducer(
    frameReducer,
    convert(ahuDesignerManager),
  );

  useEffect(() => {
    setFrameThickness(ahuDesignerManager.frameThickness);
    dispatch({
      type: "FRAMES",
      value: convert(ahuDesignerManager),
    });
    setFrames(frames);

    if (
      ahuDesignerManager.message != null &&
      ahuDesignerManager.message.length > 0
    ) {
      let msg = ahuDesignerManager.message.join("\n");
      console.log(msg);
      toast(msg);
    }
  }, [ahuDesignerManager]);

  // useEffect(() => {
  //   let finalFrames = calculateAttached(frames);
  //   const sectionIds = [...new Set(finalFrames.filter((f) => f.sectionId != null).map((f) => f.sectionId))];
  //   for (let i = 0; i < sectionIds.length; i++) {
  //     const sectionFrames = finalFrames.filter((f) => f.sectionId === sectionIds[i]);
  //     finalFrames = toggleHasFrame(true, COMPONENT_ALIGNMENT.LEFT, finalFrames, [sectionFrames[0].id], frameThickness);
  //     finalFrames = toggleHasFrame(false, COMPONENT_ALIGNMENT.RIGHT, finalFrames, [sectionFrames[0].id], frameThickness);
  //     finalFrames = toggleHasFrame(false, COMPONENT_ALIGNMENT.LEFT, finalFrames, [sectionFrames[sectionFrames.length - 1].id], frameThickness);
  //     finalFrames = toggleHasFrame(true, COMPONENT_ALIGNMENT.RIGHT, finalFrames, [sectionFrames[sectionFrames.length - 1].id], frameThickness);
  //     if (sectionFrames.length > 2) {
  //       for (let j = 1; j < sectionFrames.length - 1; j++) {
  //         finalFrames = toggleHasFrame(false, COMPONENT_ALIGNMENT.LEFT, finalFrames, [sectionFrames[j].id], frameThickness);
  //         finalFrames = toggleHasFrame(false, COMPONENT_ALIGNMENT.RIGHT, finalFrames, [sectionFrames[j].id], frameThickness);
  //       }
  //     }
  //   }
  //   dispatch({
  //     type: "FRAMES",
  //     value: finalFrames,
  //   });

  // }, []);

  // console.log(frames);

  const [isOrthographic, setIsOrthographic] = useState(false);
  const [autoRotate, setAutoRotate] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cursor, setCursor] = useState("move");
  const [pickup, setPickup] = useState();
  const [draggingEnabled, setDraggingEnabled] = useState(false);
  const [dimensionSide, setDimensionSide] = useState(1);
  const controlsRef = useRef(null);

  function change() {
    if (!controlsRef.current) return;
    const angle = controlsRef.current.getAzimuthalAngle();
    setDimensionSide(angle > 0 ? 1 : -1);
  }

  useEffect(() => {
    if (controlsRef.current) controlsRef.current.autoRotate = autoRotate;
  }, [autoRotate]);

  useEffect(() => {
    setCursor(toolMode == TOOL_MODE.MOVE ? "move" : "default");
  }, [toolMode]);

  function resetControls() {
    controlsRef.current.position0.set(0, 0.25, 8);
    controlsRef.current.target0.set(0, 0.25, 0);
    controlsRef.current.reset();
  }

  function updateUnit(type, id, value) {
    dispatch({ type: type, id: id, value: value });
  }

  const addFrame = (component, position, length, height, width, placing) => {
    const isHeatExchanger =
      component === COMPONENT_TYPES.PLATE_HEAT_EXCHANGER ||
      component === COMPONENT_TYPES.ROTARY_HEAT_EXCHANGER;
    const isFilter = component === COMPONENT_TYPES.FILTER;

    const newLength = length || (isHeatExchanger ? 750 : 500);
    const newHeight = height || (isHeatExchanger ? 1000 : 500);
    const newWidth = width || 500;

    let newPosition = [0, fromMM(newHeight / 2), 0];

    if (frames.length > 0) {
      const furthestObject = frames.reduce((prev, current) =>
        prev.position[0] > current.position[0] ? prev : current,
      );
      newPosition = furthestObject.position;
      newPosition[0] += fromMM(furthestObject.length * 1.5);
      newPosition[1] = fromMM(newHeight / 2);
    }

    updateUnit(
      "FRAMES",
      -1,
      frames.concat({
        id: THREE.MathUtils.generateUUID(),
        position: position || newPosition,
        length: newLength,
        height: newHeight,
        width: newWidth,
        connections: connections[
          component === COMPONENT_TYPES.ROTARY_HEAT_EXCHANGER
            ? 2
            : component === COMPONENT_TYPES.PLATE_HEAT_EXCHANGER
              ? 1
              : 0
        ].map((c) => {
          return { ...c, id: THREE.MathUtils.generateUUID() };
        }),
        hasLeftFrame: !isFilter,
        hasRightFrame: !isFilter,
        hasFrame: true,
        hasFloor: true,
        hasCeiling: true,
        hasLeftWall: false,
        hasRightWall: false,
        component: component,
        //placing: placing == undefined || placing,
      }),
    );
  };

  return (
    <>
      <Header
        isOrthographic={isOrthographic}
        setIsOrthographic={setIsOrthographic}
        autoRotate={autoRotate}
        setAutoRotate={setAutoRotate}
        resetControls={resetControls}
        draggingEnabled={draggingEnabled}
        setDraggingEnabled={setDraggingEnabled}
        frames={frames}
        updateUnit={updateUnit}
      />
      <div style={{ display: "flex", cursor: cursor }}>
        <Viewport>
          <Canvas
            flat
            dpr={[1, 2]}
            onPointerMissed={(e) => {
              if (
                toolMode == TOOL_MODE.SELECTION &&
                !e?.target?.className?.includes("btn-add-component") &&
                !e?.target?.className?.includes("floating-btn")
              ) {
                setSelectedUUID(null);
                setFramesAttachedTogether(false);
                setSelectedUUIDIsInSection(false);
                setShowAddComponentOptions(false);
                setSelectedConnectionMenu(null);
                setSelectedComponentInstance(null);
              }
            }}
          >
            {/* <axesHelper args={[2000]} /> */}
            <PerspectiveCamera
              position={[0, 0.25, 8]}
              fov={25}
              makeDefault={!isOrthographic}
            />
            <OrthographicCamera
              position={[0, 0, 8]}
              zoom={200}
              makeDefault={isOrthographic}
            />
            <ambientLight />
            <pointLight position={[10, 20, 10]} intensity={1} />
            <Dimensions
              enabled={showDimensions}
              frames={frames}
              dimensionSide={dimensionSide}
            ></Dimensions>
            <Unit
              frames={frames}
              draggingEnabled={draggingEnabled}
              setPickup={setPickup}
              pickup={pickup}
              isDragging={isDragging}
              setIsDragging={setIsDragging}
              updateUnit={updateUnit}
              addFrame={addFrame}
              test={""}
            />
            <ContactShadows
              rotation-x={Math.PI / 2}
              position={[0, 0, 0]}
              opacity={0.5}
              length={10}
              height={10}
              blur={0.2}
              far={0.8}
            />
            <OrbitControls
              onChange={change}
              attach={"orbitControls"}
              makeDefault
              ref={controlsRef}
              enabled={!isDragging}
              target={[0, 0.25, 0]}
            />
          </Canvas>
        </Viewport>
      </div>
    </>
  );
}

const connections = [
  [
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [1],
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [1],
    },
  ],
  [
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [1, 0],
      direction: DIRECTION_EXTRACT,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [0, 1],
      direction: DIRECTION_SUPPLY,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [1, 0],
      direction: DIRECTION_SUPPLY,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [0, 1],
      direction: DIRECTION_EXTRACT,
      lockedDirection: true,
    },
  ],
  [
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [1, 0],
      direction: DIRECTION_EXTRACT,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [0, 1],
      direction: DIRECTION_SUPPLY,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [1, 0],
      direction: DIRECTION_EXTRACT,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [0, 1],
      direction: DIRECTION_SUPPLY,
      lockedDirection: true,
    },
  ],
];

const example = JSON.parse(
  '[{"id":"24230d7b-7d4e-4864-8e55-56b899ef4c10","position":[-0.994346020755282,0.28850001096725464,0],"length":500,"height":500,"width":500,"connections":[{"side":-1,"position":[1],"id":"f1690351-9d1e-4a42-8d75-18be5812e0d0","attachedTo":null,"direction":null},{"side":1,"position":[1],"id":"16133ce5-57fb-4b9e-83f6-d77aeccbc812","attachedTo":"870717d6-64b9-4fe9-bedf-efc00daf90af","direction":null}],"door": { "visible": true }, "hasLeftFrame":true,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":true,"hasRightWall":false,"component":-1},{"id":"997eee24-df39-46c6-bb90-449fa74f038d","position":[-0.42434602075528194,0.28850001096725464,0],"length":500,"height":500,"width":500,"connections":[{"side":-1,"position":[1],"id":"870717d6-64b9-4fe9-bedf-efc00daf90af","attachedTo":"16133ce5-57fb-4b9e-83f6-d77aeccbc812","direction":null},{"side":1,"position":[1],"id":"a728b63c-79b4-4dae-829c-30b36064192d","attachedTo":"ca882fcf-6529-4e58-9ea2-66f812a6e1a9","direction":null}],"door": { "visible": true },"hasLeftFrame":true,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":-1},{"id":"19c2c70b-4937-4351-90f6-18529e9a754c","position":[0.8406539792447182,0.28850001096725464,0],"length":750,"height":500,"width":500,"connections":[{"id":"218e6c0f-c61f-4fa0-8bf1-2769da1429a4","side":-1,"position":[1],"attachedTo":"af646b06-bf12-49cf-8b58-818f26daf553","direction":null},{"id":"bcf64593-7651-4d8c-a854-0c6bb371193e","side":1,"position":[1],"attachedTo":null,"direction":null}],"door": { "visible": true, "length": 400  },"hasLeftFrame":true,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":3,"hasFlange":true,"flangeAlignment":1},{"id":"5a845aa2-8aa8-477e-a4d4-438ea943d224","position":[0.14565397924471812,0.28850001096725464,0],"length":500,"height":500,"width":500,"connections":[{"id":"ca882fcf-6529-4e58-9ea2-66f812a6e1a9","side":-1,"position":[1],"attachedTo":"a728b63c-79b4-4dae-829c-30b36064192d","direction":null},{"id":"af646b06-bf12-49cf-8b58-818f26daf553","side":1,"position":[1],"attachedTo":"218e6c0f-c61f-4fa0-8bf1-2769da1429a4","direction":null}],"door": { "visible": true },"hasLeftFrame":true,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":-1}]',
);

export default AHUDesigner;
